import { lazy, Suspense } from "react";
import { Outlet, RouterProvider, createBrowserRouter, redirect, type RouteObject } from "react-router";
import GeneralError from "./components/Layout/Error/GeneralError";
import ConnectEmailCallback from "./components/Auth/ConnectEmailCallback";
import { queryClient, QueryOptions } from "./clients/ReactQueryClient.ts";
import PurchaserClient from "./clients/PurchaserClient.ts";
import { RFQ_LIST_FILTERS, RFQ_SORT, rfqListQuery } from "./components/RFQ/List/useRFQListFilters";
import { parseFilterParams, parseSortParams } from "./components/Common/Filters";
import type { RFQListResponse } from "./clients/Purchaser/RFQ.ts";
import { parseCatalogFilterParams } from "./components/ProductCatalog/List/useCatalogListFilters";
import { parseSourcingRequestFilterParams } from "./components/SourcingRequests/List/useSourcingRequestsFilters";
import IndividualEntityPage from "./components/Layout/Page/IndividualEntityPage";
import RouteFallback from "./components/Layout/Page/RouteFallback";
import AppReady from "./components/Layout/Page/AppReady";
import MagicToast from "./components/DesignSystem/MagicToast";
import FeatureGate from "./components/Layout/FeatureGate";
import { FEATURE_KEYS } from "./util/useFeatureAccess";

// Layout pages
const SidebarMenuPage = lazy(() => import("./components/Layout/Page/SidebarMenuPage"));
const ProtectedRoute = lazy(() => import("./components/Layout/ProtectedRoute"));

// Error pages
const NotFound = lazy(() => import("./components/Layout/Error/404"));
const Maintenance = lazy(() => import("./components/Layout/Error/Maintenance"));

// Auth pages
const Login = lazy(() => import("./components/Auth/Login"));
const ForgotPassword = lazy(() => import("./components/Auth/Password/ForgotPassword"));
const Register = lazy(() => import("./components/Auth/Register"));
const ResetPassword = lazy(() => import("./components/Auth/Password/ResetPassword"));
const UserSettingsPage = lazy(() => import("./components/Auth/UserSettingsPage"));
const AcceptInvite = lazy(() => import("./components/Auth/AcceptInvite"));

// Team pages
const TeamMembersPage = lazy(() => import("./components/Settings/Team/TeamMembers"));
const CompanyDetailsPage = lazy(() => import("./components/Settings/Team/CompanyDetails"));

// RFQ pages
const RFQ = lazy(() => import("./components/RFQ/RFQ"));
const RFQLineItems = lazy(() => import("./components/RFQ/RFQLineItems"));
const RFQCompose = lazy(() => import("./components/RFQ/RFQCompose"));
const RFQFromDocument = lazy(() => import("./components/RFQ/Draft/FromDocument/RFQFromDocument"));
const RFQConfigure = lazy(() => import("./components/RFQ/RFQConfigure"));
const RFQSummary = lazy(() => import("./components/RFQ/RFQSummary"));
const RFQAwards = lazy(() => import("./components/RFQ/RFQAwards.tsx"));
const RFQInbox = lazy(() => import("./components/RFQ/PostSend/InboxV2/Inbox.tsx"));
const RFQQuotes = lazy(() => import("./components/RFQ/PostSend/Quotes/RFQQuotes.tsx"));
const RFQComparisonLineItems = lazy(() => import("./components/RFQ/PostSend/Quotes/QuotesItems"));
const RFQLeveling = lazy(() => import("./components/RFQ/PostSend/Leveling/RFQLeveling"));
const RFQList = lazy(() => import("./components/RFQ/List/RFQList"));
const PurchaseOrder = lazy(() => import("./components/PurchaseOrder/Page"));
const PurchaseOrderOverview = lazy(() => import("./components/PurchaseOrder/Overview"));
const PurchaseOrderMessages = lazy(() => import("./components/PurchaseOrder/Messages"));
const PurchaseOrderRFQDetails = lazy(() => import("./components/PurchaseOrder/RFQDetails"));
const PurchaseOrderIndexPage = lazy(() => import("./components/PurchaseOrder/List/PurchaseOrderIndexPage"));
const CreateRFQRedirect = lazy(() => import("./components/RFQ/RFQNew"));
const MailboxSources = lazy(() => import("./components/RFQ/PostSend/Tabular/MailboxSources"));
const MailboxItems = lazy(() => import("./components/RFQ/PostSend/Tabular/MailboxItems"));
const RFQVendorPage = lazy(() => import("./components/RFQ/VendorPage/VendorPage"));

// RFQ Awards
const AwardsAwarded = lazy(() => import("./components/RFQ/PostSend/Awards/AwardsAwarded.tsx"));
const AwardsPending = lazy(() => import("./components/RFQ/PostSend/Awards/AwardsPending.tsx"));
const AwardsRejected = lazy(() => import("./components/RFQ/PostSend/Awards/AwardsRejected.tsx"));

// RFQ Templates pages
const TemplateView = lazy(() => import("./components/RFQ/Templates/TemplateView"));

// Contacts and Vendors
const Contacts = lazy(() => import("./components/ContactsAndVendors/Contacts"));
const Vendors = lazy(() => import("./components/ContactsAndVendors/Vendors/Vendors"));
const VendorEdit = lazy(() => import("./components/ContactsAndVendors/Vendors/VendorEdit"));
const ContactLists = lazy(() => import("./components/ContactsAndVendors/ContactLists"));
const ViewContactList = lazy(() => import("./components/ContactsAndVendors/ViewContactList"));
const ManageContactVendorAttributes = lazy(() => import("./components/ContactsAndVendors/Manage/AttributesPage"));
const AllVendorPerformanceDashboard = lazy(() => import("./components/ContactsAndVendors/Vendors/AllVendorPerformanceDashboard"));

// Product Catalog
const CatalogList = lazy(() => import("./components/ProductCatalog/List/CatalogList"));
const CatalogItemView = lazy(() => import("./components/ProductCatalog/View/CatalogItemView"));

// Sourcing Requests
const SourcingRequestsList = lazy(() => import("./components/SourcingRequests/List/SourcingRequestsList"));
const SourcingRequestView = lazy(() => import("./components/SourcingRequests/View/SourcingRequestView"));

// Requisitions
const RequisitionsDashboard = lazy(() => import("./components/Requisitions/List/RequisitionsDashboard"));
const RequisitionDetailView = lazy(() => import("./components/Requisitions/View/RequisitionDetailView"));

// Compliance Audit
const ComplianceAudit = lazy(() => import("./components/ComplianceAudit/ComplianceAudit"));

const VendorDiscovery = lazy(() => import("./components/VendorDiscovery/VendorDiscoveryPage"));
const VendorDiscoveryFullPage = lazy(() => import("./components/VendorDiscovery/VendorDiscoveryFullPage"));
const Home = lazy(() => import("./components/Home/index"));
const CustomizableHome = lazy(() => import("./components/Home/CustomizableHome"));
const WidgetShowcase = lazy(() => import("./components/Dashboard/WidgetShowcase"));
// Local development only. The build replaces import.meta.env.DEV with false, which removes the page from production.
const DesignSystemPage = import.meta.env.DEV ? lazy(() => import("./components/Showcase/DesignSystemPage")) : null;
const CustomDashboard = lazy(() => import("./components/Dashboard/CustomDashboard"));
const DashboardIndex = lazy(() => import("./components/Dashboard/DashboardIndex"));

// Documents Hub (DH)
const DHDocuments = lazy(() => import("./components/DocumentsHub/Hub/Documents"));
const DHDocument = lazy(() => import("./components/DocumentsHub/Document/Document"));

// Threats
const Threats = lazy(() => import("./components/Threats/Threats"));

// Spend Analysis
const SpendAnalysis = lazy(() => import("./components/SpendAnalysis/SpendAnalysis"));
const SACategoryPage = lazy(() => import("./components/SpendAnalysis/SACategoryPage"));

// Document Viewer (Single Document)
const FileViewerPage = lazy(() => import("./components/Documents/FileViewerPage"));

// Settings
const Settings = lazy(() => import("./components/Settings/Settings"));

// Unsubscribe
const Unsubscribe = lazy(() => import("./components/Unsubscribe"));

// Outreach
const Outreach = lazy(() => import("./components/Outreach"));

declare global {
    // Used in Vendor Discovery Map component
    namespace google {
        const maps: any;
    }
}

export default function App() {
    // Unprotected routes that do not require authentication
    const unprotectedRoutes: RouteObject[] = [
        {
            path: "/login",
            element: <Login />,
        },
        {
            path: "/registration-internal-link/*",
            element: <Register />,
        },
        {
            path: "/forgot-password",
            element: <ForgotPassword />,
        },
        {
            path: "/reset-password",
            element: <ResetPassword />,
        },
        {
            path: "/unsubscribe",
            element: <Unsubscribe />,
        },
        {
            path: "/outreach/:uniqueUrlSlug",
            element: <Outreach />,
        },
    ];

    // Protected routes
    // These routes require authentication and will redirect to /login if not authenticated
    // They also show the sidebar menu
    const protectedSidebarRoutes: RouteObject = {
        element: (
            <ProtectedRoute>
                <SidebarMenuPage />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "/",
                index: true,
                element: <Home />,
            },
            {
                path: "/home-custom",
                element: <CustomizableHome />,
            },
            {
                path: "/widget-library",
                element: <WidgetShowcase />,
            },
            {
                path: "/dashboards",
                element: <DashboardIndex />,
            },
            {
                path: "/dashboards/:dashboardId",
                element: <CustomDashboard />,
            },
            {
                path: "rfqs",
                index: true,
                element: <RFQList />,
                loader: ({ request }) => {
                    const url = new URL(request.url);
                    // Built the same way useRFQList builds it, so the prefetch lands in the list's own cache entry.
                    const query = rfqListQuery(parseFilterParams(RFQ_LIST_FILTERS, url.searchParams), parseSortParams(RFQ_SORT, url.searchParams));
                    queryClient.prefetchInfiniteQuery({
                        queryKey: ["rfqList", "infinite", query],
                        queryFn: async ({ pageParam }): Promise<RFQListResponse> => {
                            return await PurchaserClient.getRFQList({ ...query, page: pageParam, limit: 50 });
                        },
                        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
                            if (!lastPage?.pagination) return undefined;
                            const { totalPages } = lastPage.pagination;
                            return lastPageParam < totalPages ? lastPageParam + 1 : undefined;
                        },
                        initialPageParam: 1,
                        pages: 1,
                    });
                },
            },
            {
                path: "/purchase-orders",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.PO_MANAGEMENT}>
                        <PurchaseOrderIndexPage />
                    </FeatureGate>
                ),
            },
            {
                path: "/contacts",
                element: <Contacts />,
            },
            {
                path: "/contacts/lists",
                index: true,
                element: <ContactLists />,
            },
            {
                path: "/vendors",
                element: <Vendors />,
            },
            {
                path: "/vendors/performance",
                element: <AllVendorPerformanceDashboard />,
            },
            {
                path: "/spend-analysis",
                index: true,
                element: <SpendAnalysis />,
            },
            {
                path: "/spend-analysis/categories/:categoryId",
                element: <SACategoryPage />,
            },
            {
                path: "/vendor-discovery",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.VENDOR_DISCOVERY}>
                        <VendorDiscovery />
                    </FeatureGate>
                ),
            },
            {
                path: "/documents-hub",
                index: true,
                element: <DHDocuments />,
            },
            {
                path: "/catalog",
                index: true,
                element: <CatalogList />,
                loader: ({ request }) => {
                    const url = new URL(request.url);
                    const filters = parseCatalogFilterParams(url.searchParams);
                    queryClient.prefetchQuery(QueryOptions.CatalogList(filters));
                },
            },
            {
                path: "/sourcing-requests",
                index: true,
                element: <SourcingRequestsList />,
                loader: ({ request }) => {
                    const url = new URL(request.url);
                    const filters = parseSourcingRequestFilterParams(url.searchParams);
                    queryClient.prefetchQuery(QueryOptions.SourcingRequestList(filters));
                },
            },
            {
                path: "/requisitions",
                index: true,
                element: (
                    <FeatureGate feature={FEATURE_KEYS.REQUISITIONS}>
                        <RequisitionsDashboard />
                    </FeatureGate>
                ),
            },
            {
                path: "/compliance-audit",
                element: <ComplianceAudit />,
            },
            ...(DesignSystemPage !== null ? [{ path: "/design-system", element: <DesignSystemPage /> }] : []),
            // Threats
            {
                path: "/threats",
                index: true,
                element: <Threats />,
            },
        ],
    };

    // Protected routes for individual entities (no sidebar)
    const protectedIndividualEntityRoutes: RouteObject = {
        element: (
            <ProtectedRoute>
                <IndividualEntityPage />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "/user-settings",
                element: <UserSettingsPage />,
            },
            {
                path: "/invite/accept",
                element: <AcceptInvite />,
            },
            {
                path: "/vendor-discovery/:uniquePublicId",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.VENDOR_DISCOVERY}>
                        <VendorDiscoveryFullPage />
                    </FeatureGate>
                ),
            },
            {
                path: "/team/members",
                element: <TeamMembersPage />,
            },
            {
                path: "/team/company-details",
                element: <CompanyDetailsPage />,
            },

            // Purchase order detail: full-width, no sidebar, like the RFQ entity page.
            {
                path: "/purchase-orders/:purchaseOrderId",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.PO_MANAGEMENT}>
                        <PurchaseOrder />
                    </FeatureGate>
                ),
                children: [
                    { index: true, element: <PurchaseOrderOverview /> },
                    { path: "messages", element: <PurchaseOrderMessages /> },
                    { path: "rfq-details", element: <PurchaseOrderRFQDetails /> },
                ],
            },

            // RFQs
            {
                path: "rfqs",
                element: <RFQ />,
                children: [
                    {
                        // Laying out the path this way gives us the URL structure like this:
                        // /rfqs/:rfqPublicId/compose
                        // /rfqs/:rfqPublicId/configure
                        // /rfqs/:rfqPublicId
                        path: ":rfqPublicId",
                        loader: async ({ params }) => {
                            try {
                                const { rfqPublicId } = params;
                                if (!rfqPublicId) throw new Error("No ID");
                                queryClient.prefetchQuery(QueryOptions.RFQ(rfqPublicId));
                                queryClient.prefetchQuery(QueryOptions.RFQMailboxes(rfqPublicId));
                                return { rfqPublicId };
                            } catch {
                                // Redirect to RFQ list if invalid ID
                                return redirect("/rfqs");
                            }
                        },
                        children: [
                            {
                                path: "line-items",
                                element: <RFQLineItems />,
                            },
                            {
                                path: "compose",
                                element: <RFQCompose />,
                            },
                            {
                                path: "from-document",
                                element: <RFQFromDocument />,
                            },
                            {
                                path: "configure",
                                element: <RFQConfigure />,
                            },
                            {
                                path: "summary",
                                element: <RFQSummary />,
                            },
                            {
                                path: "inbox",
                                element: <RFQInbox />,
                            },
                            {
                                path: "inbox/:mailboxId",
                                element: <RFQInbox />,
                            },
                            {
                                path: "quotes",
                                element: <RFQQuotes />,
                            },
                            {
                                path: "leveling",
                                element: <RFQLeveling />,
                            },
                            {
                                path: "leveling/*",
                                element: <RFQLeveling />,
                            },
                            {
                                path: "awards",
                                element: <RFQAwards />,
                                children: [
                                    {
                                        index: true,
                                        loader: () => redirect("awarded"),
                                    },
                                    {
                                        path: "awarded",
                                        element: <AwardsAwarded />,
                                    },
                                    {
                                        path: "pending",
                                        element: <AwardsPending />,
                                    },
                                    {
                                        path: "rejected",
                                        element: <AwardsRejected />,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },

            {
                path: "rfqs/:rfqPublicId",
                children: [
                    {
                        path: "quotes/:vendorId",
                        element: <RFQQuotes />,
                    },
                    {
                        path: "inbox/:mailboxId/tabular",
                        element: <MailboxItems />,
                    },
                    {
                        path: "vendors/:mailboxId",
                        element: <RFQVendorPage />,
                        handle: { noGutters: true },
                    },
                    {
                        path: "vendors/:mailboxId/:section",
                        element: <RFQVendorPage />,
                        handle: { noGutters: true },
                    },
                    {
                        path: "inbox/:mailboxId/tabular/source",
                        element: <MailboxSources />,
                    },
                    {
                        path: "comparison/line-items",
                        element: <RFQComparisonLineItems />,
                    },
                    {
                        path: "leveling",
                        element: <RFQLeveling />,
                    },
                    {
                        path: "leveling/*",
                        element: <RFQLeveling />,
                    },
                ],
            },

            // Vendor View / Edit
            {
                path: "/vendors/:vendorRelationshipId",
                children: [
                    {
                        index: true,
                        element: <VendorEdit />,
                    },
                    {
                        path: "details",
                        element: <VendorEdit />,
                    },
                    {
                        path: "contacts",
                        element: <VendorEdit />,
                    },
                    {
                        path: "indexed-profile",
                        element: <VendorEdit />,
                    },
                    {
                        path: "documents",
                        element: <VendorEdit />,
                    },
                    {
                        path: "rfqs",
                        element: <VendorEdit />,
                    },
                    {
                        path: "performance-dashboard",
                        element: <VendorEdit />,
                    },
                ],
            },

            // Product Catalog Item View
            {
                path: "/catalog/:catalogItemId",
                children: [
                    {
                        index: true,
                        element: <CatalogItemView />,
                    },
                    {
                        path: "details",
                        element: <CatalogItemView />,
                    },
                    {
                        path: "rfq-history",
                        element: <CatalogItemView />,
                    },
                    {
                        path: "sourcing-requests",
                        element: <CatalogItemView />,
                    },
                ],
            },

            // Sourcing Request View
            {
                path: "/sourcing-requests/:sourcingRequestId",
                element: <SourcingRequestView />,
            },

            // Requisition View
            {
                path: "/requisitions/:id",
                element: <RequisitionDetailView />,
            },

            // Contact Lists
            {
                path: "/contacts",
                children: [
                    {
                        path: "lists/:contactListId",
                        element: <ViewContactList />,
                    },
                ],
            },

            // Contacts and Vendors - Attributes Management
            {
                path: "/manage/attributes/contacts-vendors",
                element: <ManageContactVendorAttributes />,
            },

            // Canonical document viewer — handles every supported mimetype
            // (PDF, image, spreadsheet, 2D/3D geometry, extracted markdown)
            // and the document-memory fact overlay flow when ?highlights= is present.
            {
                path: "/documents/:id/:base64InternalFilename",
                element: <FileViewerPage />,
            },

            // Unified Settings
            {
                path: "/settings",
                children: [
                    {
                        index: true,
                        element: <Settings />,
                    },
                    {
                        path: "user/profile",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications/rfq-subscriptions",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications/rfq-assignment",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications/requisitions",
                        element: <Settings />,
                    },
                    {
                        path: "sourcing/labels",
                        element: <Settings />,
                    },
                    {
                        path: "sourcing/categories",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/attributes",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/templates",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/templates/:templateId",
                        element: <TemplateView />,
                    },
                    {
                        path: "rfq/webhooks",
                        element: <Settings />,
                    },
                    {
                        path: "vendors/attributes",
                        element: <Settings />,
                    },
                    {
                        path: "vendors/performance-scoring",
                        element: <Settings />,
                    },
                    {
                        path: "comparison/topics",
                        element: <Settings />,
                    },
                    {
                        path: "comparison/default-config",
                        element: <Settings />,
                    },
                    {
                        path: "leveling-views/configs",
                        element: <Settings />,
                    },
                    {
                        path: "team/members",
                        element: <Settings />,
                    },
                    {
                        path: "team/instructions",
                        element: <Settings />,
                    },
                    {
                        path: "team/company-details",
                        element: <Settings />,
                    },
                    {
                        path: "team/semantic-columns",
                        element: <Settings />,
                    },
                    {
                        path: "requisitions/configuration",
                        element: <Settings />,
                    },
                    {
                        path: "security/email-activity",
                        element: <Settings />,
                    },
                    {
                        path: "security/trust-center",
                        element: <Settings />,
                    },
                    {
                        path: "security/api-keys",
                        element: <Settings />,
                    },
                ],
            },

            // Document Hub Document View
            {
                path: "/documents-hub/:id/:base64InternalFilename/:originalFilename",
                element: <DHDocument />,
            },
        ],
    };

    // Additional Programmatic Routes
    // These routes are for things like redirects or routes that don't fit into the above categories
    const additionalProgrammaticRoutes: RouteObject = {
        element: (
            <ProtectedRoute>
                <Outlet />
            </ProtectedRoute>
        ),
        children: [
            // Connect your email callback
            {
                path: "/connect-email/oauth",
                element: <ConnectEmailCallback />,
            },

            // RFQ v2 to v3 Redirects
            {
                path: "/sourcing/rfqs",
                errorElement: <GeneralError backTo="/rfqs" backToLabel="RFQs" />,
                children: [
                    {
                        index: true,
                        loader: () => redirect("/rfqs"),
                    },
                    {
                        path: ":rfqPublicId",
                        loader: () => redirect("/rfqs"),
                    },
                    {
                        path: ":rfqPublicId/quotes",
                        loader: () => redirect("/rfqs"),
                    },
                    {
                        path: ":rfqPublicId/threads",
                        loader: () => redirect("/rfqs"),
                    },
                ],
            },

            // Create a new RFQ redirect
            {
                path: "/rfqs/new",
                element: <CreateRFQRedirect />,
            },
        ],
    };

    const routes: RouteObject[] = [
        {
            path: "/",
            element: (
                <Suspense fallback={<RouteFallback />}>
                    <AppReady />
                    <MagicToast.Container />
                    <Outlet />
                </Suspense>
            ),
            errorElement: <GeneralError backTo="/" backToLabel="purchaser" />,
            children: [
                ...unprotectedRoutes,
                protectedSidebarRoutes,
                protectedIndividualEntityRoutes,
                additionalProgrammaticRoutes,
                // Catch-all 404 (inside "/" so it renders under <AppReady />). No inner Suspense:
                // let the chunk suspend the "/" boundary so the HTML app-shell loader bridges the
                // load, instead of handing off to a second spinner.
                { path: "*", element: <NotFound /> },
            ],
        },
    ];

    // Maintenance Mode Routes
    // If the app is in maintenance mode these routes will be used instead
    // to show a maintenance page for all routes
    const isMaintenanceMode = false;
    const maintenanceRoutes: RouteObject[] = [
        {
            path: "/*",
            element: (
                <Suspense>
                    <Maintenance />
                </Suspense>
            ),
        },
    ];

    const router = createBrowserRouter(isMaintenanceMode ? maintenanceRoutes : routes);

    return <RouterProvider router={router} />;
}
